import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { softSx, heroSoftSx, tint, captionSx } from './dashboardUtils';

/** The greeting panel. `title` is the greeting, `subtitle` the date line, `actions` the period switch. */
export const SoftHero = ({ title, subtitle, actions, children }) => {
  const theme = useTheme();
  return (
    <Box sx={heroSoftSx(theme)}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontSize: { xs: '1.0625rem', md: '1.125rem' }, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25 }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
        {actions}
      </Box>
      {children && <Box sx={{ mt: 4 }}>{children}</Box>}
    </Box>
  );
};

/** Card heading with an optional coloured icon chip and a right-hand slot. */
export const SoftTitle = ({ icon: Icon, fill, title, sub, right }) => {
  const theme = useTheme();
  const t = fill ? tint(theme, fill) : null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: 0 }}>
        {Icon && t && (
          <Box sx={{ width: 36, height: 36, borderRadius: 2.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: t.bg, color: t.ink }}>
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 }}>{title}</Typography>
          {sub && <Typography noWrap sx={{ ...captionSx, fontSize: '0.78rem', mt: 0.25 }}>{sub}</Typography>}
        </Box>
      </Box>
      {right}
    </Box>
  );
};

/** Small rounded status pill in a tint. */
export const Pill = ({ fill = 'slate', children, dot = true }) => {
  const theme = useTheme();
  const t = tint(theme, fill);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 0.75, borderRadius: 999, bgcolor: t.bg, color: t.ink, fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
      {dot && <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.ink }} />}
      {children}
    </Box>
  );
};

/** Centred empty state for a card with nothing to show. */
export const EmptyState = ({ icon: Icon, fill = 'slate', title, sub }) => {
  const theme = useTheme();
  const t = tint(theme, fill);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 8, px: 4, minHeight: 180, height: '100%' }}>
      {Icon && (
        <Box sx={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: t.bg, color: t.ink, mb: 3 }}>
          <Icon sx={{ fontSize: 26 }} />
        </Box>
      )}
      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 700 }}>{title}</Typography>
      {sub && <Typography sx={{ ...captionSx, fontSize: '0.8125rem', mt: 0.75, maxWidth: 280 }}>{sub}</Typography>}
    </Box>
  );
};

/** A soft card. */
export const SoftCard = ({ fill, sx, children }) => {
  const theme = useTheme();
  return <Box sx={{ ...softSx(theme, fill), ...sx }}>{children}</Box>;
};
